import React from 'react';
import {List, Typography} from 'antd';
import PodExemptStatusTag from './PodExemptStatusTag';
import {useLazyQuery} from '@apollo/client';
import type {DocumentNode} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import {PodExemptNearbyItemType} from '../../graphql/podExempt/types';

interface Props {
  /** report.id（Long 标量字符串，后端 parseValue 接受） */
  reportId: string;
  /** 端各自的 nearby query（wms/qc） */
  query: DocumentNode;
  /** 响应字段名（wmsPodExemptNearbyEffective / qcPodExemptNearbyEffective） */
  responseKey: string;
  /** 端各自的额外变量（仓管侧需带 warehouseId 供仓权限校验；QC 侧不传） */
  extraVariables?: Record<string, unknown>;
  /** 审核页：进页即查（原型形态）；抽屉场景保持点开才查省请求 */
  autoLoad?: boolean;
  /** 审核页放在独立卡片里，隐藏组件自带标题 */
  hideTitle?: boolean;
  /** 审核页：点关联上报单号跳该单审核页（只读）；不传则单号为纯文本 */
  onOpenReport?: (reportId: string) => void;
  /** 原因 code → 字典名（原型 meta「距离 · 原因」）；不传时回退展示目标类型 */
  dictNameOf?: (code: string) => string;
}

/** 500m 参考：默认点开才查（1 次请求），列出周边已生效豁免目标辅助审核判断。 */
const PodExemptNearbyPanel: React.FC<Props> = ({
  reportId,
  query,
  responseKey,
  extraVariables,
  autoLoad = false,
  hideTitle = false,
  onOpenReport,
  dictNameOf,
}) => {
  const {t} = useTranslation();
  // 原型形态：标题为地址/围栏名（围栏加「（围栏）」后缀），meta 为「距离 · 上报原因」
  const titleOf = (item: PodExemptNearbyItemType) =>
    `${item.display ?? `#${item.targetId}`}${item.targetType === 'FENCE' ? t('podExempt.nearby.fenceSuffix') : ''}`;
  const metaOf = (item: PodExemptNearbyItemType) => {
    const reason = item.reasonCode ? (dictNameOf?.(item.reasonCode) ?? item.reasonCode) : null;
    const fallback = item.targetType === 'FENCE' ? t('podExempt.scope.fence') : t('podExempt.scope.single');
    return `${item.distanceMeters}m · ${reason ?? fallback}`;
  };
  const [fetchNearby, {data, loading, called, error}] = useLazyQuery<Record<string, PodExemptNearbyItemType[]>>(
    query,
    {variables: {reportId, ...extraVariables}, fetchPolicy: 'network-only'},
  );
  React.useEffect(() => {
    if (autoLoad && !called) {
      fetchNearby();
    }
  }, [autoLoad, called, fetchNearby]);

  const items = data?.[responseKey] ?? [];

  return (
    <div className={hideTitle ? '' : 'mt-4'}>
      {!hideTitle && (
        <Typography.Title level={5}>
          {t('podExempt.nearby.title')}{' '}
          {(!called || error) && (
            <Typography.Link onClick={() => fetchNearby()}>
              {error ? t('podExempt.nearby.retry') : t('podExempt.nearby.load')}
            </Typography.Link>
          )}
        </Typography.Title>
      )}
      {hideTitle && error && (
        <Typography.Link onClick={() => fetchNearby()}>{t('podExempt.nearby.retry')}</Typography.Link>
      )}
      {/* 加载失败不能显示「附近无目标」的假空态（审核人会据此误判），给出错误提示 + 重试入口 */}
      {called && error && (
        <Typography.Text type="danger" className="text-xs">
          {t('podExempt.nearby.loadFailed')}
        </Typography.Text>
      )}
      {called && !error && (
        <List
          size="small"
          loading={loading}
          dataSource={items}
          locale={{emptyText: t('podExempt.nearby.empty')}}
          renderItem={(item) =>
            hideTitle ? (
              // 审核页原型形态：边框圆角条 + 右侧单号结论与状态 tag
              <div className="border border-gray-100 rounded-md px-3.5 py-2.5 mb-2 flex items-center justify-between gap-3">
                <span className="min-w-0 truncate">
                  <span className="font-medium">{titleOf(item)}</span>
                  <span className="text-gray-400">
                    {'  '}
                    {metaOf(item)}
                  </span>
                </span>
                <span className="flex-none flex items-center gap-2">
                  {/* 展示关联上报单号（队列单框可 ER-x 精确搜；明细 ID 是下游豁免用的，审核域不可查） */}
                  <span className="text-gray-400 text-xs">
                    {onOpenReport ? (
                      <Typography.Link
                        className="!text-xs"
                        onClick={() => onOpenReport(item.reportId)}
                      >
                        ER-{item.reportId}
                      </Typography.Link>
                    ) : (
                      <>ER-{item.reportId}</>
                    )}
                  </span>
                  <PodExemptStatusTag status={item.status} effectiveLibrary />
                </span>
              </div>
            ) : (
              <List.Item extra={<PodExemptStatusTag status={item.status} effectiveLibrary />}>
                <span>
                  <span className="font-medium">{titleOf(item)}</span>
                  <span className="text-gray-400"> {metaOf(item)}</span>
                </span>
              </List.Item>
            )
          }
        />
      )}
    </div>
  );
};

export default PodExemptNearbyPanel;
